import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TQuickAction, TPracticeOverviewSummaryVo } from './types';
import AISummary from './components/AISummary';
import PerformanceOverview from './components/PerformanceOverview';
import QuickActions from './components/QuickActions';
import AttentionNeeded from './components/AttentionNeeded';
import FinancialPerformance from './components/FinancialPerformance';
import ProviderPerformance from './components/ProviderPerformance';
import PatientInsights from './components/PatientInsights';
import AppointmentsSection from './components/AppointmentsSection';
import AddAndEditStaff from 'components/AddAndEditStaff';
import AddProviderModal from 'components/AddProviderModal';
import { PATH } from 'constants/path';
import { openManageSubscriptionModal } from 'utils/globalLayerControl';
import s from './s.module.less';
import { canShowTeamManagementAction, canShowTeamManagementView } from 'utils/common';
import useCurrentUser from 'hooks/useCurrentUser';

const PracticeOverviewPage: React.FC = () => {
    const [summary, setSummary] = useState<TPracticeOverviewSummaryVo | null>(null);
    const [showAddStaffModal, setShowAddStaffModal] = useState(false);
    const [showAddProviderModal, setShowAddProviderModal] = useState(false);
    const navigate = useNavigate();
    const [currentUser] = useCurrentUser();
    const showTeamManagementAction = canShowTeamManagementAction(currentUser);
    const quickActions: TQuickAction[] = useMemo(
        () => [
            { label: 'Manage my team', action: () => openManageSubscriptionModal('9') },
            ...(showTeamManagementAction
                ? [
                      {
                          label: 'Invite new team members',
                          subItems: [
                              { label: 'Add staff', action: () => setShowAddStaffModal(true) },
                              { label: 'Add providers', action: () => setShowAddProviderModal(true) },
                          ],
                      },
                  ]
                : []),
            { label: 'Manage my practice', action: () => openManageSubscriptionModal('7') },
            { label: 'View financials', action: () => navigate(`${PATH.DASHBOARD}/${PATH.FINANCIALS}`) },
            { label: 'Access help center', action: () => navigate(`${PATH.DASHBOARD}/${PATH.HELP_CENTER}`) },
        ],
        [navigate, showTeamManagementAction],
    );

    return (
        <div className={s.wrap}>
            <div className={s.pageHdr}>
                <h1 className={s.pageTitle}>Practice overview</h1>
            </div>

            <AISummary />

            <div className={s.twoCol}>
                <div className={s.colWide}>
                    <PerformanceOverview onSummaryChange={setSummary} />
                </div>
                <div className={s.colNarrow}>
                    <QuickActions actions={quickActions} />
                </div>
            </div>

            <AttentionNeeded />

            <FinancialPerformance summary={summary} />

            <ProviderPerformance />

            <AppointmentsSection summary={summary} />

            <PatientInsights />

            {showAddStaffModal && (
                <AddAndEditStaff
                    show={showAddStaffModal}
                    onCannel={() => setShowAddStaffModal(false)}
                    onSuccess={() => setShowAddStaffModal(false)}
                />
            )}
            <AddProviderModal open={showAddProviderModal} onClose={() => setShowAddProviderModal(false)} />
        </div>
    );
};

export default PracticeOverviewPage;
